import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ImportApi, ImportJobState } from "api/features/import/ops";
import { importKeys } from "api/features/import/queries";
import { useUserContext } from "contexts/contextUser";

export const GLOBAL_ACTIVE_IMPORT_JOB_STORAGE_KEY = "activeImportJob";

export type StoredActiveImportJob = {
  jobId: string;
  label: string;
};

type ImportActiveJobContextValue = {
  activeJob: ImportJobState | null;
  activeJobId: string | null;
  isAnotherImportRunning: (localJobId: string | null) => boolean;
  refetchActiveJob: () => void;
  /** Stops active-job polling while this job is tracked via GET /imports/jobs/:jobId */
  registerLocalJobPolling: (jobId: string | null, isPolling: boolean) => void;
};

const ImportActiveJobContext = createContext<ImportActiveJobContextValue | null>(null);

export function readStoredActiveImportJob(): StoredActiveImportJob | null {
  const raw = localStorage.getItem(GLOBAL_ACTIVE_IMPORT_JOB_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredActiveImportJob;
    if (!parsed?.jobId || !parsed?.label) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeStoredActiveImportJob(jobId: string, label: string): void {
  localStorage.setItem(
    GLOBAL_ACTIVE_IMPORT_JOB_STORAGE_KEY,
    JSON.stringify({ jobId, label } satisfies StoredActiveImportJob)
  );
}

export function clearStoredActiveImportJob(): void {
  localStorage.removeItem(GLOBAL_ACTIVE_IMPORT_JOB_STORAGE_KEY);
}

export function ImportActiveJobProvider({ children }: { children: ReactNode }): JSX.Element {
  const { accessToken } = useUserContext();
  const queryClient = useQueryClient();
  const [locallyPolledJobIds, setLocallyPolledJobIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const registerLocalJobPolling = useCallback((jobId: string | null, isPolling: boolean) => {
    if (!jobId) return;
    setLocallyPolledJobIds((prev) => {
      const has = prev.has(jobId);
      if (isPolling === has) return prev;
      const next = new Set(prev);
      if (isPolling) next.add(jobId);
      else next.delete(jobId);
      return next;
    });
  }, []);

  const activeJobQuery = useQuery({
    queryKey: importKeys.active,
    queryFn: ({ signal }) => {
      if (!accessToken) throw new Error("Access token is required");
      return ImportApi.getActiveImportJob(accessToken, signal);
    },
    enabled: Boolean(accessToken),
    refetchInterval: (query) => {
      const job = query.state.data?.job;
      const activeId = job?.jobId;

      if (activeId && !job.finishedAt && locallyPolledJobIds.has(activeId)) {
        return false;
      }

      if (!job) return 30000;
      if (!job.finishedAt) return 5000;
      return 30000;
    },
  });

  const activeJob = activeJobQuery.data?.job ?? null;
  const activeJobId = activeJob?.jobId ?? null;

  const value = useMemo<ImportActiveJobContextValue>(
    () => ({
      activeJob,
      activeJobId,
      isAnotherImportRunning: (localJobId) =>
        Boolean(activeJobId && activeJobId !== localJobId && !activeJob?.finishedAt),
      refetchActiveJob: () => {
        void queryClient.invalidateQueries({ queryKey: importKeys.active });
      },
      registerLocalJobPolling,
    }),
    [activeJob, activeJobId, queryClient, registerLocalJobPolling]
  );

  return (
    <ImportActiveJobContext.Provider value={value}>{children}</ImportActiveJobContext.Provider>
  );
}

export function useImportActiveJob(): ImportActiveJobContextValue {
  const context = useContext(ImportActiveJobContext);
  if (!context) {
    throw new Error("useImportActiveJob must be used within ImportActiveJobProvider");
  }
  return context;
}
